import { useState, useEffect, useRef } from "react";
import parse from "react-html-parser";
import { Row, Col, Button } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router";
import { toast } from "react-toastify";

import "./AccountSettings.css";

import InputField from "@/components/common/InputField/InputField";
import {
  changePassword,
  getUserSmsOptInDetails,
  checkValidEmailAddress,
  getUserProfile,
  saveUserSmsOptInFlag,
  checkDeletedUserEmail,
  getUserSmsOptInMessage,
} from "@/api/myProfile";
import { generateRandomID } from "@/utils/generateRandomID";
import { encrypt } from "@/utils/crypto";
import { getLoginSignupCMS, getRequestId } from "@/api/LoginSignup";
import { getWebCMS } from "@/api/PDP";
import { formatCmsTextToHtml } from "@/utils/textParser";
import { validateForm } from "@/utils/formValidator";
import LoadingText from "@/components/loader/LoadingText";
import LoadedText from "@/components/loader/LoadedText";
import useAuth from "@/components/common/header/useAuth";
import ResetPasswordModal from "@/components/password-reset/ResetPasswordModal";
import DashboardSectionWrapper from "../components/DashboardSectionWrapper";
import { BiLoaderCircle } from "react-icons/bi";
import { trackApiCall, trackButtonClick } from "@/utils/analytics";

const AccountSettings = () => {
  const formRefs = useRef({});

  const navigate = useNavigate();
  const location = useLocation();

  const shouldFocusPasswordField = location.state?.focusPasswordField;

  const { isUserLoggedIn, userSignOut } = useAuth();
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [optOutMessage, setOptOutMessage] = useState("");
  const [userData, setUserData] = useState("");
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSmsPrefUpdating, setIsSmsPrefUpdating] = useState(false);
  const [blurredFields, setBlurredFields] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });
  const [errors, setErrors] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
    phoneNumber: "",
  });

  const [passwords, setPasswords] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [webCMS, setWebCMS] = useState(null);
  const [loginCmsData, setLoginCmsData] = useState(null);

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;

    setPasswords((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));

    setBlurredFields((prev) => ({
      ...prev,
      [name]: false,
    }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const passwordFields = [
      "currentPassword",
      "newPassword",
      "confirmPassword",
    ];

    setBlurredFields((prev) => ({
      ...prev,
      [name]: true,
    }));

    // Check if the field is empty
    if (value === "" && passwordFields.includes(name)) {
      setErrors((prev) => ({
        ...prev,
        [name]: "This field is required.",
      }));
      return;
    }

    // If value is not empty, validate using validateForm
    if (value !== "" && passwordFields.includes(name)) {
      // Create a temporary object with just the current field to validate
      const fieldToValidate = { [name]: value };

      // Validate the single field
      const validationErrors = validateForm(
        fieldToValidate,
        [name],
        false,
        true
      );

      // Special case for confirmPassword - check if it matches newPassword
      if (
        name === "confirmPassword" &&
        passwords.newPassword &&
        value !== passwords.newPassword
      ) {
        validationErrors.confirmPassword =
          "New password and confirm password do not match";
      }

      // Update errors state if there are validation errors
      if (Object.keys(validationErrors).length > 0) {
        setErrors((prev) => ({
          ...prev,
          ...validationErrors,
        }));
      }
    }
  };

  useEffect(() => {
    if (shouldFocusPasswordField) {
      formRefs.current["newPassword"]?.focus();
      // Add smooth scroll to the profile name field
      formRefs.current["newPassword"]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [shouldFocusPasswordField]);

  useEffect(() => {
    let isMounted = true;

    const fetchInitialData = async () => {
      try {
        setLoading(true);

        // Fetching Data in parallel
        const [
          userProfileResponse,
          smsOptInResponse,
          webCMSResponse,
          loginCmsResponse,
        ] = await Promise.all([
          getUserProfile(),
          getUserSmsOptInDetails(),
          getWebCMS("pages/userDashboard/accountSettings"),
          getLoginSignupCMS("pages/loginSignup"),
        ]);

        if (!isMounted) return;

        // update states if component is still mounted

        if (smsOptInResponse?.status === "SUCCESS") {
          setSmsOptIn(smsOptInResponse?.data?.smsOptInStatus);
        }

        if (userProfileResponse?.data?.userDetails) {
          const userDetails = userProfileResponse.data.userDetails;

          setUserData(userDetails);

          // Only check email validation if we have an email
          if (userDetails?.emailId) {
            const encodedPayload = `email=${encodeURIComponent(
              userDetails?.emailId
            )}`;
            // const encodedPayload = `email=${userDetails?.emailId}
            // `;
            const emailValidationRsponse = await checkValidEmailAddress(
              encodedPayload
            );
            if (
              emailValidationRsponse?.data?.emailValidation !== undefined &&
              emailValidationRsponse?.data?.emailValidation !== "VALID"
            ) {
              toast.error("Please validate your email");
            }
          }
        }

        if (webCMSResponse?.results?.[0]) {
          setWebCMS(webCMSResponse.results[0]);
        }
        if (loginCmsResponse?.results?.[0]) {
          setLoginCmsData(loginCmsResponse.results[0]);
        }
      } catch (error) {
        if (isMounted) {
          console.error("Error fetching initial data:", error);
          setErrors(error.message || "Failed to load data");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchInitialData();
  }, []);

  const handleMobileEdit = () => {
    trackButtonClick({
      eventName: "edit_mobile_number_click",
      eventCategory: "Account Settings",
      eventSource: "on edit mobile click",
    });
    navigate("/my-shopper/myProfile", { state: { focusPhoneField: true } });
  };

  const handleSmsOptInChange = async (checked) => {
    setOptOutMessage("");
    try {
      setIsSmsPrefUpdating(true);
      setSmsOptIn(checked);

      trackButtonClick({
        eventName: "opt_for_sms_toggle",
        eventParams: { opt_status: checked ? "in" : "out" },
        eventCategory: "Account Settings",
        eventSource: "account settings opt in for sms",
      });

      const response = await saveUserSmsOptInFlag({
        phoneNumber: userData?.phoneNumber,
        endPoint: checked ? "saveUserSmsOptInFlag" : "saveUserSmsOptOutFlag",
      });

      if (response?.status === "SUCCESS") {
        toast.success("SMS preference updated successfully");
        trackApiCall({
          eventName: "opt_for_sms_saved",
          eventParams: { opt_status: checked ? "in" : "out" },
          eventCategory: "Account Settings",
          eventSource: "account settings opt in for sms",
        });
        if (checked) {
          await getUserSmsOptInMessage();
        } else {
          let result = await getUserSmsOptInMessage();
          setOptOutMessage(result?.data || "");
        }
      } else {
        setSmsOptIn(!checked);
        toast.error("Failed to update SMS preference");
      }
    } catch (error) {
      setSmsOptIn(!checked);
      setErrors((prev) => ({
        ...prev,
        smsOptIn: "Failed to update SMS preference",
      }));
      console.error("Error updating SMS preference:", error);
      toast.error("Error updating SMS preference");
    } finally {
      setIsSmsPrefUpdating(false);
    }
  };

  const UpdateNewPassword = async () => {
    try {
      // validate mobile number
      if (!userData?.phoneNumber) {
        setErrors((prev) => ({
          ...prev,
          phoneNumber: "Phone number is required",
        }));
        return;
      }

      const validationErrors = validateForm(
        passwords,
        userData?.isSocialSignIn
          ? ["newPassword", "confirmPassword"] // For social login users
          : ["currentPassword", "newPassword", "confirmPassword"], // For regular users
        false,
        true
      );

      if (
        passwords.newPassword &&
        passwords.confirmPassword &&
        passwords.newPassword !== passwords.confirmPassword
      ) {
        validationErrors.confirmPassword =
          "New password and confirm password do not match";
      }

      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }

      setErrors((prevErr) => ({
        ...prevErr,
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      }));

      trackButtonClick({
        eventName: "change_password_click",
        eventCategory: "BuyerDashboard",
        eventSource: "Update password click",
      });

      setIsLoading(true);

      const requestId = generateRandomID();
      await getRequestId({ requestId: requestId });
      const [encryptedOldPassword, encryptedNewPassword] = await Promise.all([
        encrypt(passwords.currentPassword),
        encrypt(passwords.newPassword),
      ]);

      const payload = {
        cryptoAlgVersion: "V3",
        oldPassword: encryptedOldPassword,
        newPassword: encryptedNewPassword,
        requestId: requestId,
      };

      const response = await changePassword(payload);

      if (response.SUCCESS) {
        setPasswords({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
        setBlurredFields({
          currentPassword: false,
          newPassword: false,
          confirmPassword: false,
        });
        toast.success("Password updated successfully");
        userSignOut({ isLoginModalRequired: true });

        trackApiCall({
          eventName: "change_password_success",
          eventCategory: "BuyerDashboard",
          eventSource: "Update password api call",
        });
      } else if (response.ERROR) {
        trackApiCall({
          eventName: "change_password_failure",
          eventCategory: "BuyerDashboard",
          eventSource: "Update password api call",
        });
        if (response.ERROR.includes("May not be one of your previous")) {
          const text =
            "Password must be: Between 8 and 20 characters long. Include a combination of uppercase and lowercase letters, numbers, and at least one of the following special characters: ! # @ % $ ; ^ May not be one of your previous 12 passwords or contain your screen name.";
          toast.error(text);
        } else toast.error(response.ERROR);
      }
    } catch (error) {
      console.log("Failed to update password");
      toast.error(error?.message || "Failed to update password");
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPasswordModal = async () => {
    try {
      const encodedEmail = encodeURIComponent(userData?.emailId);
      const deletedUserCheck = await checkDeletedUserEmail(
        `emailId=${encodedEmail}`
      );
      if (
        deletedUserCheck?.data?.data === false &&
        deletedUserCheck?.data?.status === "SUCCESS"
      ) {
        setShowPasswordResetModal(true);
        trackButtonClick({
          eventName: "forgot_password_link_click",
          eventCategory: "New-Registration",
          eventSource: "forgot password click",
        });
      } else {
        toast.error("Please validate your email");
      }
    } catch (error) {
      toast.error(error?.message || "Failed to validate email");
    }
  };

  return (
    <DashboardSectionWrapper title="Account settings">
      <div className="mx-2">
        <ResetPasswordModal
          userData={userData}
          show={showPasswordResetModal}
          onHide={() => {
            setShowPasswordResetModal(false);
            trackButtonClick({
              eventName: "forgot_password_modal_closed",
              eventCategory: "Account Settings",
              eventSource: "Close Button",
              eventLabel: "Password Reset Modal",
            });
          }}
          termsLink={loginCmsData?.terms}
          privacyLink={loginCmsData?.privacyPolicy}
        />
        <div className="border-bottom">
          <div className="p-2 settings-subtitle">Mobile Notifications</div>

          {/* Mobile Field Section */}
          <div className="mt-3 account-left-margin">
            <Row className="align-items-center">
              <Col sm={10}>
                <InputField
                  type="tel"
                  className="mb-0"
                  width="62%"
                  label="Mobile Number"
                  name="account_mobile"
                  key="phone"
                  value={userData?.phoneNumber}
                  isEditButtonEnabled={true}
                  onEdit={handleMobileEdit}
                  error={errors.phoneNumber}
                  isProfileLoading={loading}
                  disabled
                  inputwidth
                  inputwidthClass={"w-60"}
                />
              </Col>
            </Row>

            {/* SMS Toggle Switch */}
            <Row
              className="mb-3 align-items-center mobile-responsive-account-padding"
              style={{ paddingLeft: "17%" }}
            >
              <Col sm={12}>
                <div className="settings-slider-switch">
                  <label className="switch settings-switch">
                    <input
                      type="checkbox"
                      className="settings-input-checkbox"
                      id="smsOptInSwitch-account"
                      checked={smsOptIn}
                      onChange={(e) => handleSmsOptInChange(e.target.checked)}
                      disabled={isLoading}
                    />
                    <span
                      className={`settings-slider round ${
                        smsOptIn ? "slider-bg-success" : "slider-bg-secondary"
                      }`}
                    >
                      <span className="yes">Yes</span>
                      <span className="no">No</span>
                    </span>
                  </label>
                  {isSmsPrefUpdating && <BiLoaderCircle />}
                  <div
                    className={`settings-slider-status-text ${
                      smsOptIn ? "text-success" : "text-secondary"
                    }`}
                  >
                    {smsOptIn
                      ? "Yes, I would like to receive text message notifications on my bidding activities."
                      : "No, I do not want to receive text message notifications on my bidding activities."}
                  </div>
                </div>

                {smsOptIn ? (
                  <div className="ms-2 my-2">
                    <small
                      className="text-muted settings-status-text"
                      onClick={() => {
                        trackButtonClick({
                          eventName: "terms_and_conditions_click",
                          eventCategory: "Account Settings",
                          eventSource: "SMS Terms and Conditions",
                        });
                      }}
                    >
                      {parse(formatCmsTextToHtml(webCMS?.smsOptinHelpText))}
                    </small>
                  </div>
                ) : (
                  <div className="ms-2 my-2">
                    <small className="text-muted settings-status-text">
                      {optOutMessage ||
                        parse(formatCmsTextToHtml(webCMS?.smsOptOutHelpText))}
                    </small>
                  </div>
                )}
              </Col>
            </Row>
          </div>
        </div>

        {/* Password Section */}
        <div className="mt-4 account-left-margin">
          <div className="account-section-title">
            {userData?.isSocialSignIn ? "Set Password" : "Change Password"}
          </div>

          {/* Current Password */}
          {!userData?.isSocialSignIn && (
            <Row className="mb-1 align-items-center">
              <Col sm={10}>
                <InputField
                  type="password"
                  placeholder="Current Password"
                  className="mb-0"
                  label="Current Password"
                  width="62%"
                  autoComplete="off"
                  required
                  forgotPwdEnabled
                  value={passwords.currentPassword}
                  name={"currentPassword"}
                  key="currentPassword"
                  onChange={handlePasswordChange}
                  handleBlur={handleBlur}
                  isEditButtonEnabled={false}
                  isProfileLoading={loading}
                  error={errors.currentPassword}
                  handleForgotPasswordModal={handleForgotPasswordModal}
                  fieldStyle={
                    blurredFields.currentPassword &&
                    !errors.currentPassword &&
                    passwords.currentPassword
                      ? {
                          borderBottom: "2px solid rgb(49, 208, 4)",
                        }
                      : {}
                  }
                />
              </Col>
            </Row>
          )}

          {/* New Password */}
          <Row className="mb-1 align-items-center">
            <Col sm={10}>
              <InputField
                formRef={formRefs}
                type="password"
                placeholder="New Password"
                label="New Password"
                className="mb-0"
                showToolTip={true}
                width="62%"
                autoComplete="off"
                required
                value={passwords.newPassword}
                name={"newPassword"}
                key="newPassword"
                isProfileLoading={loading}
                onChange={handlePasswordChange}
                handleBlur={handleBlur}
                isEditButtonEnabled={false}
                error={errors.newPassword}
                fieldStyle={
                  blurredFields.newPassword &&
                  !errors.newPassword &&
                  passwords.newPassword
                    ? {
                        borderBottom: "2px solid rgb(49, 208, 4)",
                      }
                    : {}
                }
              />
            </Col>
          </Row>

          {/* Confirm Password */}
          <Row className="mb-1 align-items-center">
            <Col sm={10}>
              <InputField
                type="password"
                showRightIcon={false}
                label="Confirm Password"
                className="mb-0"
                placeholder="Confirm Password"
                width="62%"
                autoComplete="off"
                required
                value={passwords.confirmPassword}
                key="confirmPassword"
                name={"confirmPassword"}
                isProfileLoading={loading}
                onChange={handlePasswordChange}
                handleBlur={handleBlur}
                isEditButtonEnabled={false}
                error={errors.confirmPassword}
                fieldStyle={
                  blurredFields.confirmPassword &&
                  !errors.confirmPassword &&
                  passwords.confirmPassword
                    ? {
                        borderBottom: "2px solid rgb(49, 208, 4)",
                      }
                    : {}
                }
              />
            </Col>
          </Row>

          {/* Update Button */}
          <Row
            className="mb-5 align-items-center mobile-responsive-account-padding"
            style={{ paddingLeft: "17%" }}
          >
            <Col sm={10}>
              <Button
                className="btn btn-primary setting-submit-btn"
                onClick={UpdateNewPassword}
              >
                {isLoading ? (
                  <LoadingText text="Updating..." />
                ) : (
                  <LoadedText text="Update Password" />
                )}
              </Button>
            </Col>
          </Row>
        </div>
      </div>
    </DashboardSectionWrapper>
  );
};

export default AccountSettings;
